// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import type {MessageSwipeActionId} from '@app/features/accessibility/state/MessageSwipeActionPrefs';
import {
	createMessageActionHandlers,
	getEffectiveContent,
	getMessagePermissions,
	isClientSystemMessage,
	type MessagePermissions,
	requestDeleteMessage,
	requestRemindMessage,
	triggerAddReaction,
} from '@app/features/channel/components/MessageActionUtils';
import type {Channel} from '@app/features/channel/models/Channel';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {goBackOr} from '@app/features/platform/components/router/NavigationAdapter';
import {MessageStates} from '@fluxer/constants/src/ChannelConstants';
import type {I18n} from '@lingui/core';

export function navigateToMobileChannelList(channel: Channel, pathname: string): void {
	if (channel.isDM() || channel.isGroupDM() || channel.isPersonalNotes()) {
		RouterUtils.transitionTo(Routes.ME);
		return;
	}
	if (Routes.isFavoritesRoute(pathname)) {
		RouterUtils.transitionTo(Routes.FAVORITES);
		return;
	}
	if (channel.guildId) {
		NavigationCommands.selectChannel(channel.guildId);
		return;
	}
	goBackOr(Routes.ME);
}

/**
 * Mirrors MessageActionMenu / MessageActionBar gates for swipeable actions.
 * Client-system messages are never actionable (except open_channel_list).
 */
export function isMessageSwipeActionAvailable(
	action: MessageSwipeActionId,
	message: Message,
	permissions: MessagePermissions | null = getMessagePermissions(message),
): boolean {
	if (action === 'none') {
		return false;
	}
	if (action === 'open_channel_list') {
		return true;
	}
	if (!permissions || isClientSystemMessage(message)) {
		return false;
	}
	if (message.state === MessageStates.FAILED) {
		return action === 'delete';
	}
	if (message.state !== MessageStates.SENT) {
		return false;
	}
	switch (action) {
		case 'reply':
			return message.isUserMessage() && permissions.canSendMessages;
		case 'react':
			return permissions.canAddReactions;
		case 'edit':
			return message.isUserMessage() && permissions.canEditMessage && !message.messageSnapshots;
		case 'delete':
			return permissions.canDeleteMessage;
		case 'forward':
			return message.isUserMessage() && permissions.canForwardMessage;
		case 'pin':
			return message.isUserMessage() && permissions.canPinMessage;
		case 'bookmark':
			return message.isUserMessage();
		case 'remind':
			return message.isUserMessage();
		case 'mark_unread':
			return true;
		case 'copy_message':
			return getEffectiveContent(message).length > 0;
		default:
			return false;
	}
}

export interface ExecuteMessageSwipeActionOptions {
	i18n: I18n;
	message: Message;
	channel: Channel;
	pathname: string;
	permissions?: MessagePermissions | null;
}

export function executeMessageSwipeAction(
	action: MessageSwipeActionId,
	options: ExecuteMessageSwipeActionOptions,
): boolean {
	const permissions = options.permissions ?? getMessagePermissions(options.message, options.channel);
	if (!isMessageSwipeActionAvailable(action, options.message, permissions)) {
		return false;
	}
	if (action === 'open_channel_list') {
		navigateToMobileChannelList(options.channel, options.pathname);
		return true;
	}
	if (action === 'react') {
		triggerAddReaction(options.message);
		return true;
	}
	const handlers = createMessageActionHandlers(options.message, {
		i18n: options.i18n,
		channel: options.channel,
	});
	switch (action) {
		case 'reply':
			handlers.handleReply();
			return true;
		case 'edit':
			handlers.handleEditMessage();
			return true;
		case 'delete':
			if (options.message.state === MessageStates.FAILED) {
				handlers.handleFailedMessageDelete();
			} else {
				requestDeleteMessage(options.message, options.i18n);
			}
			return true;
		case 'forward':
			handlers.handleForward();
			return true;
		case 'pin':
			handlers.handlePinMessage();
			return true;
		case 'bookmark':
			handlers.handleSaveMessage(SavedMessages.isSaved(options.message.id))();
			return true;
		case 'remind':
			requestRemindMessage(options.message, options.i18n);
			return true;
		case 'mark_unread':
			handlers.handleMarkAsUnread();
			return true;
		case 'copy_message':
			handlers.handleCopyMessage();
			return true;
		default:
			return false;
	}
}
